'use strict'

import {
  decorateLeafletPathDualDash
} from './portal-dual-dash.js'
import {
  bearingBetweenLatLng,
  offsetLatLngByMeters
} from './portal-geo-utils.js'

const GRID_REFERENCE_VERSION = 3
const GRID_OFFSET_ABS_MAX_PX = 16384
const GRID_SHIFT_STEP_DEFAULT_M = 0.05
const GRID_SHIFT_STEP_MIN_M = 0.01
const GRID_SHIFT_STEP_MAX_M = 100
const GRID_SHIFT_STEP_STORAGE_KEY = 'portal-grid-helper-shift-step-m-v1'
const GRID_ROTATION_STEP_DEFAULT_DEG = 0.05
const GRID_ROTATION_STEP_MIN_DEG = 0.01
const GRID_ROTATION_STEP_MAX_DEG = 45
const GRID_ROTATION_STEP_STORAGE_KEY = 'portal-grid-helper-rotate-step-deg-v1'
const GRID_SHIFT_MIN_PROJECTED_STEP = 0.01
const GRID_WAYPOINT_MIN_POINTS = 4
const GRID_WAYPOINT_MIN_DISTANCE_M = 1
const GRID_WAYPOINT_SPACING_TOLERANCE_MIN_M = 0.1
const GRID_WAYPOINT_SPACING_TOLERANCE_RATIO = 0.02
const GRID_WAYPOINT_CLUSTER_MIN_SHARE = 0.6
const GRID_WAYPOINT_RIGHT_ANGLE_TOLERANCE_DEG = 18
const GRID_WAYPOINT_CELL_PREDICTION_TOLERANCE_M = 0.35
const GRID_WAYPOINT_CORE_SHARE = 0.75

const DEFAULT_GRID_SETTINGS = {
  cellWidthM: 5,
  cellHeightM: 10,
  rotationDeg: 0,
  offsetXM: 0,
  offsetYM: 0,
  offsetXPx: 0,
  offsetYPx: 0,
  offsetZoom: null,
  anchorLat: null,
  anchorLng: null,
  rotationCenterLat: null,
  rotationCenterLng: null,
  referenceVersion: GRID_REFERENCE_VERSION,
  minZoom: 19,
  maxZoom: 30
}

const createMapGridHelper = function ({
  $ = window.jQuery,
  L = window.L,
  map = null,
  $body = ($ ? $('body') : null),
  db = {},
  store = {},
  fnT = function (key) { return String(key || '') },
  fnGetImportMissionState = function () { return null },
  fnOnStateChange = function () {}
} = {}) {
  if (!$ || !L || !map || !$body || typeof $body.find !== 'function') {
    return {
      layer: null,
      openSettings: function () { return null },
      activate: function () { return false },
      getSettings: function () { return Object.assign({}, DEFAULT_GRID_SETTINGS) },
      getNearestGridPoint: function () { return null },
      getNearestAxisBearing: function () { return NaN },
      getVisibilityState: function () {
        return {
          overlayEnabled: false,
          isVisible: false,
          zoomState: 'unknown',
          currentZoom: NaN,
          minZoom: DEFAULT_GRID_SETTINGS.minZoom,
          maxZoom: DEFAULT_GRID_SETTINGS.maxZoom
        }
      }
    }
  }

  const fnNormalizeFinite = function (value, fallback) {
    const num = Number(value)
    return Number.isFinite(num) ? num : fallback
  }

  const fnNormalizePositive = function (value, fallback) {
    const num = fnNormalizeFinite(value, fallback)
    return num > 0 ? num : fallback
  }

  const fnNormalizeOptionalFinite = function (value, fallback = null) {
    if (value === null || value === undefined) return fallback
    if (typeof value === 'string' && !value.trim()) return fallback
    const num = Number(value)
    return Number.isFinite(num) ? num : fallback
  }

  const fnIsEditableElement = function (element) {
    if (!element || typeof element !== 'object') return false
    const tagName = String(element.tagName || '').toLowerCase()
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return true
    return element.isContentEditable === true
  }

  const fnNormalizeGridSettings = function (rawSettings) {
    const next = Object.assign({}, DEFAULT_GRID_SETTINGS, rawSettings || {})
    const rawReferenceVersion = Number((rawSettings || {}).referenceVersion)
    next.cellWidthM = fnNormalizePositive(next.cellWidthM, DEFAULT_GRID_SETTINGS.cellWidthM)
    next.cellHeightM = fnNormalizePositive(next.cellHeightM, DEFAULT_GRID_SETTINGS.cellHeightM)
    next.rotationDeg = fnNormalizeFinite(next.rotationDeg, DEFAULT_GRID_SETTINGS.rotationDeg)
    next.offsetXM = fnNormalizeFinite(next.offsetXM, DEFAULT_GRID_SETTINGS.offsetXM)
    next.offsetYM = fnNormalizeFinite(next.offsetYM, DEFAULT_GRID_SETTINGS.offsetYM)
    next.offsetXPx = fnNormalizeFinite(next.offsetXPx, DEFAULT_GRID_SETTINGS.offsetXPx)
    next.offsetYPx = fnNormalizeFinite(next.offsetYPx, DEFAULT_GRID_SETTINGS.offsetYPx)
    next.offsetZoom = fnNormalizeOptionalFinite(next.offsetZoom, null)
    next.anchorLat = fnNormalizeOptionalFinite(next.anchorLat, null)
    next.anchorLng = fnNormalizeOptionalFinite(next.anchorLng, null)
    next.rotationCenterLat = fnNormalizeOptionalFinite(next.rotationCenterLat, null)
    next.rotationCenterLng = fnNormalizeOptionalFinite(next.rotationCenterLng, null)
    if (Math.abs(next.offsetXPx) > GRID_OFFSET_ABS_MAX_PX) {
      next.offsetXPx = DEFAULT_GRID_SETTINGS.offsetXPx
    }
    if (Math.abs(next.offsetYPx) > GRID_OFFSET_ABS_MAX_PX) {
      next.offsetYPx = DEFAULT_GRID_SETTINGS.offsetYPx
    }
    if (rawReferenceVersion !== GRID_REFERENCE_VERSION) {
      next.offsetXM = DEFAULT_GRID_SETTINGS.offsetXM
      next.offsetYM = DEFAULT_GRID_SETTINGS.offsetYM
      next.offsetXPx = DEFAULT_GRID_SETTINGS.offsetXPx
      next.offsetYPx = DEFAULT_GRID_SETTINGS.offsetYPx
      next.offsetZoom = null
      next.anchorLat = null
      next.anchorLng = null
      next.rotationCenterLat = null
      next.rotationCenterLng = null
    }
    next.referenceVersion = GRID_REFERENCE_VERSION
    next.minZoom = fnNormalizeFinite(next.minZoom, DEFAULT_GRID_SETTINGS.minZoom)
    next.maxZoom = fnNormalizeFinite(next.maxZoom, DEFAULT_GRID_SETTINGS.maxZoom)
    if (next.minZoom > next.maxZoom) {
      const temp = next.minZoom
      next.minZoom = next.maxZoom
      next.maxZoom = temp
    }
    next.minZoom = Math.max(0, next.minZoom)
    next.maxZoom = Math.max(next.minZoom, next.maxZoom)
    return next
  }

  const fnNormalizeShiftStepMeters = function (value, fallback = GRID_SHIFT_STEP_DEFAULT_M) {
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) return fallback
    const clamped = Math.min(GRID_SHIFT_STEP_MAX_M, Math.max(GRID_SHIFT_STEP_MIN_M, parsed))
    return Math.round(clamped * 100) / 100
  }

  const fnReadShiftStepMetersFromStorage = function () {
    if (typeof window === 'undefined' || !window.localStorage) return GRID_SHIFT_STEP_DEFAULT_M
    try {
      const raw = window.localStorage.getItem(GRID_SHIFT_STEP_STORAGE_KEY)
      return fnNormalizeShiftStepMeters(raw, GRID_SHIFT_STEP_DEFAULT_M)
    } catch (_error) {
      return GRID_SHIFT_STEP_DEFAULT_M
    }
  }

  const fnWriteShiftStepMetersToStorage = function (value) {
    const normalized = fnNormalizeShiftStepMeters(value, GRID_SHIFT_STEP_DEFAULT_M)
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(GRID_SHIFT_STEP_STORAGE_KEY, String(normalized))
      } catch (_error) {}
    }
    return normalized
  }

  const fnNormalizeRotateStepDeg = function (value, fallback = GRID_ROTATION_STEP_DEFAULT_DEG) {
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) return fallback
    const clamped = Math.min(GRID_ROTATION_STEP_MAX_DEG, Math.max(GRID_ROTATION_STEP_MIN_DEG, parsed))
    return Math.round(clamped * 100) / 100
  }

  const fnReadRotateStepDegFromStorage = function () {
    if (typeof window === 'undefined' || !window.localStorage) return GRID_ROTATION_STEP_DEFAULT_DEG
    try {
      const raw = window.localStorage.getItem(GRID_ROTATION_STEP_STORAGE_KEY)
      return fnNormalizeRotateStepDeg(raw, GRID_ROTATION_STEP_DEFAULT_DEG)
    } catch (_error) {
      return GRID_ROTATION_STEP_DEFAULT_DEG
    }
  }

  const fnWriteRotateStepDegToStorage = function (value) {
    const normalized = fnNormalizeRotateStepDeg(value, GRID_ROTATION_STEP_DEFAULT_DEG)
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(GRID_ROTATION_STEP_STORAGE_KEY, String(normalized))
      } catch (_error) {}
    }
    return normalized
  }

  const fnEnsureGridSettingsStore = function () {
    if (!db.settings || typeof db.settings !== 'object') db.settings = {}
    if (!db.settings.gridHelper || typeof db.settings.gridHelper !== 'object') {
      db.settings.gridHelper = Object.assign({}, DEFAULT_GRID_SETTINGS)
    }
    db.settings.gridHelper = fnNormalizeGridSettings(db.settings.gridHelper)
    return db.settings.gridHelper
  }

  const fnPersistGridSettings = function () {
    fnEnsureGridSettingsStore()
    if (store && typeof store.setAll === 'function') {
      void store.setAll(db)
    }
  }

  const state = {
    settings: fnNormalizeGridSettings(fnEnsureGridSettingsStore()),
    redrawFrame: 0,
    shiftStepMeters: fnReadShiftStepMetersFromStorage(),
    rotateStepDeg: fnReadRotateStepDegFromStorage(),
    rotationCenterMarker: null,
    modalOpen: false,
    modalKeydownCapture: null,
    modalKeyupCapture: null
  }

  if (!map.getPane('gridHelperPane')) {
    map.createPane('gridHelperPane')
  }
  const gridPane = map.getPane('gridHelperPane')
  if (gridPane) {
    gridPane.style.zIndex = '430'
    gridPane.style.pointerEvents = 'none'
  }

  const layer = L.layerGroup()

  const fnGetSwal = function () {
    if (typeof window === 'undefined') return null
    return window.PORTAL_AREA_SWAL || window.Swal || null
  }

  const fnToRad = function (deg) {
    return Number(deg) * (Math.PI / 180)
  }

  const fnNormalizeBearing = function (value) {
    const num = Number(value)
    if (!Number.isFinite(num)) return NaN
    let out = num % 360
    if (out < 0) out += 360
    return out
  }

  const fnSignedBearingDiff = function (fromDeg, toDeg) {
    const from = fnNormalizeBearing(fromDeg)
    const to = fnNormalizeBearing(toDeg)
    if (!Number.isFinite(from) || !Number.isFinite(to)) return NaN
    return ((to - from + 540) % 360) - 180
  }

  const fnBearingToRotationDeg = function (bearingDeg) {
    const bearing = fnNormalizeBearing(bearingDeg)
    if (!Number.isFinite(bearing)) return NaN
    return fnNormalizeBearing(90 - bearing)
  }

  const fnRotatePoint = function (point, angleDeg) {
    const rad = fnToRad(angleDeg)
    const cosA = Math.cos(rad)
    const sinA = Math.sin(rad)
    return L.point(
      (point.x * cosA) - (point.y * sinA),
      (point.x * sinA) + (point.y * cosA)
    )
  }

  const fnPointLength = function (point) {
    return Math.sqrt((point.x * point.x) + (point.y * point.y))
  }

  const fnNormalizePoint = function (point) {
    const len = fnPointLength(point)
    if (!(len > 0)) return L.point(0, 0)
    return L.point(point.x / len, point.y / len)
  }

  const fnTF = function (key, vars, fallback) {
    const text = String(fnT(key) || fallback || key || '')
    const data = (vars && typeof vars === 'object') ? vars : {}
    return text.replace(/\{([a-zA-Z0-9_]+)\}/g, function (_match, token) {
      return Object.prototype.hasOwnProperty.call(data, token) ? String(data[token]) : ''
    })
  }

  const fnMedian = function (values) {
    const list = Array.isArray(values)
      ? values
        .map(function (value) { return Number(value) })
        .filter(function (value) { return Number.isFinite(value) })
        .sort(function (left, right) { return left - right })
      : []
    if (!list.length) return NaN
    const middleIndex = Math.floor(list.length / 2)
    if ((list.length % 2) === 1) return list[middleIndex]
    return (list[middleIndex - 1] + list[middleIndex]) / 2
  }

  const fnGetWaypointLatLng = function (point) {
    const lat = Number(point && point.latlng && point.latlng[0])
    const lng = Number(point && point.latlng && point.latlng[1])
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return L.latLng(lat, lng)
  }

  const fnHasGridAnchor = function () {
    return Number.isFinite(state.settings.anchorLat) && Number.isFinite(state.settings.anchorLng)
  }

  const fnHasGridAnchorForSettings = function (settings) {
    const cfg = settings && typeof settings === 'object' ? settings : state.settings
    return Number.isFinite(cfg.anchorLat) && Number.isFinite(cfg.anchorLng)
  }

  const fnEnsureGridReference = function () {
    let changed = false
    if (!fnHasGridAnchor()) {
      const center = map.getCenter()
      if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
        state.settings.anchorLat = center.lat
        state.settings.anchorLng = center.lng
        changed = true
      }
    }
    if (!Number.isFinite(state.settings.offsetZoom)) {
      state.settings.offsetZoom = 0
      changed = true
    }
    if (changed) {
      if (!db.settings || typeof db.settings !== 'object') db.settings = {}
      db.settings.gridHelper = Object.assign({}, state.settings)
    }
  }

  const fnProjectLatLng = function (latlng) {
    const crs = map && map.options ? map.options.crs : null
    if (!crs || typeof crs.project !== 'function' || !latlng) return null
    return crs.project(L.latLng(latlng))
  }

  const fnUnprojectPoint = function (point) {
    const crs = map && map.options ? map.options.crs : null
    if (!crs || typeof crs.unproject !== 'function' || !point) return null
    return crs.unproject(L.point(point.x, point.y))
  }

  const fnMeasureProjectedVectorGroundDistance = function (anchorLatLng, anchorProjected, direction, projectedLength) {
    if (!anchorLatLng || !anchorProjected || !direction) return NaN
    const targetLatLng = fnUnprojectPoint(L.point(
      anchorProjected.x + (direction.x * Number(projectedLength)),
      anchorProjected.y + (direction.y * Number(projectedLength))
    ))
    if (!targetLatLng) return NaN
    return Number(map.distance(anchorLatLng, targetLatLng))
  }

  const fnBuildProjectedVectorForGroundOffset = function (anchorLatLng, northMeters, eastMeters) {
    const source = anchorLatLng && anchorLatLng.lat !== undefined && anchorLatLng.lng !== undefined
      ? anchorLatLng
      : map.getCenter()
    const targetDistance = Math.hypot(Number(northMeters) || 0, Number(eastMeters) || 0)
    if (!(targetDistance > 0)) return L.point(0, 0)
    const anchorProjected = fnProjectLatLng(source)
    if (!anchorProjected) return L.point(0, 0)
    const bearingDeg = fnNormalizeBearing(Math.atan2(Number(eastMeters) || 0, Number(northMeters) || 0) * (180 / Math.PI))
    const approxLatLngRaw = offsetLatLngByMeters(source.lat, source.lng, targetDistance, bearingDeg)
    const approxLatLng = L.latLng(approxLatLngRaw.lat, approxLatLngRaw.lng)
    const approxProjected = fnProjectLatLng(approxLatLng)
    const approxVector = approxProjected
      ? L.point(approxProjected.x - anchorProjected.x, approxProjected.y - anchorProjected.y)
      : L.point(0, 0)
    const direction = fnNormalizePoint(approxVector)
    if (!(fnPointLength(direction) > 0)) return approxVector
    let lowerLength = 0
    let upperLength = Math.max(fnPointLength(approxVector), GRID_SHIFT_MIN_PROJECTED_STEP)
    let upperGroundDistance = fnMeasureProjectedVectorGroundDistance(source, anchorProjected, direction, upperLength)
    let expandGuard = 0
    while (Number.isFinite(upperGroundDistance) && upperGroundDistance < targetDistance && expandGuard < 12) {
      lowerLength = upperLength
      upperLength *= 2
      upperGroundDistance = fnMeasureProjectedVectorGroundDistance(source, anchorProjected, direction, upperLength)
      expandGuard += 1
    }
    if (!Number.isFinite(upperGroundDistance)) return approxVector
    for (let i = 0; i < 24; i += 1) {
      const middleLength = (lowerLength + upperLength) / 2
      const middleGroundDistance = fnMeasureProjectedVectorGroundDistance(source, anchorProjected, direction, middleLength)
      if (!Number.isFinite(middleGroundDistance)) break
      if (middleGroundDistance < targetDistance) {
        lowerLength = middleLength
      } else {
        upperLength = middleLength
      }
    }
    return L.point(direction.x * upperLength, direction.y * upperLength)
  }

  const fnGetAppliedProjectedOffsetForSettings = function (settings) {
    const cfg = settings && typeof settings === 'object' ? settings : state.settings
    return {
      x: Number(cfg.offsetXM || 0),
      y: Number(cfg.offsetYM || 0)
    }
  }

  const fnGetAppliedProjectedOffset = function () {
    fnEnsureGridReference()
    return fnGetAppliedProjectedOffsetForSettings(state.settings)
  }

  const fnFormatOffsetPx = function (value) {
    const num = Number(value)
    if (!Number.isFinite(num)) return '0'
    return String(Math.round(num * 1000) / 1000)
  }

  const fnGetBasisVectorsBySettings = function (settings) {
    const cfg = settings && typeof settings === 'object'
      ? fnNormalizeGridSettings(settings)
      : state.settings
    const anchorLatLng = fnHasGridAnchorForSettings(cfg) ? L.latLng(cfg.anchorLat, cfg.anchorLng) : map.getCenter()
    const anchorProjected = fnProjectLatLng(anchorLatLng)
    const xVectorBase = fnBuildProjectedVectorForGroundOffset(anchorLatLng, 0, cfg.cellWidthM)
    const yVectorBase = fnBuildProjectedVectorForGroundOffset(anchorLatLng, cfg.cellHeightM, 0)
    if (!anchorProjected) {
      return {
        origin: L.point(0, 0),
        xVector: L.point(0, 0),
        yVector: L.point(0, 0)
      }
    }
    const appliedOffset = fnGetAppliedProjectedOffsetForSettings(cfg)
    return {
      origin: L.point(
        anchorProjected.x + appliedOffset.x,
        anchorProjected.y + appliedOffset.y
      ),
      xVector: fnRotatePoint(xVectorBase, cfg.rotationDeg),
      yVector: fnRotatePoint(yVectorBase, cfg.rotationDeg)
    }
  }

  const fnGetBasisVectors = function () {
    fnEnsureGridReference()
    return fnGetBasisVectorsBySettings(state.settings)
  }

  const fnSolveGridIndices = function (basis, targetPoint) {
    const xVector = basis && basis.xVector ? basis.xVector : null
    const yVector = basis && basis.yVector ? basis.yVector : null
    const origin = basis && basis.origin ? basis.origin : null
    if (!xVector || !yVector || !origin || !targetPoint) return null
    const det = (xVector.x * yVector.y) - (xVector.y * yVector.x)
    if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return null
    const relX = Number(targetPoint.x) - origin.x
    const relY = Number(targetPoint.y) - origin.y
    const idxX = ((relX * yVector.y) - (relY * yVector.x)) / det
    const idxY = ((xVector.x * relY) - (xVector.y * relX)) / det
    if (!Number.isFinite(idxX) || !Number.isFinite(idxY)) return null
    return { idxX: idxX, idxY: idxY }
  }

  const fnBuildGridPointFromIndices = function (basis, idxX, idxY) {
    if (!basis || !basis.origin || !basis.xVector || !basis.yVector) return null
    const point = L.point(
      basis.origin.x + (basis.xVector.x * Number(idxX)) + (basis.yVector.x * Number(idxY)),
      basis.origin.y + (basis.xVector.y * Number(idxX)) + (basis.yVector.y * Number(idxY))
    )
    return {
      idxX: Number(idxX),
      idxY: Number(idxY),
      containerPoint: point,
      latLng: fnUnprojectPoint(point)
    }
  }

  const fnGetNearestGridPointToContainerPoint = function (basis, targetPoint) {
    const indices = fnSolveGridIndices(basis, targetPoint)
    if (!indices) return null
    return fnBuildGridPointFromIndices(
      basis,
      Math.round(indices.idxX),
      Math.round(indices.idxY)
    )
  }

  const fnGetNearestGridPoint = function (latLng) {
    if (!latLng || !Number.isFinite(Number(latLng.lat)) || !Number.isFinite(Number(latLng.lng))) return null
    const targetLatLng = L.latLng(latLng.lat, latLng.lng)
    const targetPoint = fnProjectLatLng(targetLatLng)
    if (!targetPoint) return null
    const nearest = fnGetNearestGridPointToContainerPoint(fnGetBasisVectors(), targetPoint)
    if (!nearest || !nearest.latLng) return null
    const targetContainerPoint = map.latLngToContainerPoint(targetLatLng)
    const nearestContainerPoint = map.latLngToContainerPoint(nearest.latLng)
    const diffX = Number(nearestContainerPoint.x) - Number(targetContainerPoint.x)
    const diffY = Number(nearestContainerPoint.y) - Number(targetContainerPoint.y)
    return Object.assign({}, nearest, {
      distancePx: Math.sqrt((diffX * diffX) + (diffY * diffY)),
      distanceMeters: Number(map.distance(targetLatLng, nearest.latLng))
    })
  }

  const fnAddLinePair = function (fromPoint, toPoint) {
    const fromLatLng = fnUnprojectPoint(fromPoint)
    const toLatLng = fnUnprojectPoint(toPoint)
    if (!fromLatLng || !toLatLng) return
    const dashLine = decorateLeafletPathDualDash(L.polyline([fromLatLng, toLatLng], {
      pane: 'gridHelperPane',
      color: '#000000',
      weight: 1,
      opacity: 0.95,
      dashArray: '6,6',
      interactive: false,
      bubblingMouseEvents: false,
      smoothFactor: 0
    }), { L: L })
    layer.addLayer(dashLine)
  }

  const fnDotPoint = function (leftPoint, rightPoint) {
    return (leftPoint.x * rightPoint.x) + (leftPoint.y * rightPoint.y)
  }

  const fnGetVisibilityState = function () {
    const currentZoom = Number(map.getZoom())
    let zoomState = 'unknown'
    if (Number.isFinite(currentZoom)) {
      if (currentZoom < state.settings.minZoom) {
        zoomState = 'too_low'
      } else if (currentZoom > state.settings.maxZoom) {
        zoomState = 'too_high'
      } else {
        zoomState = 'visible'
      }
    }
    const overlayEnabled = map.hasLayer(layer)
    return {
      overlayEnabled: overlayEnabled,
      isVisible: overlayEnabled && zoomState === 'visible',
      zoomState: zoomState,
      currentZoom: currentZoom,
      minZoom: state.settings.minZoom,
      maxZoom: state.settings.maxZoom
    }
  }

  const fnEmitStateChange = function () {
    if (typeof fnOnStateChange === 'function') {
      fnOnStateChange(fnGetVisibilityState())
    }
  }

  const fnClearGrid = function () {
    layer.clearLayers()
  }

  const fnRedrawGrid = function () {
    state.redrawFrame = 0
    fnClearGrid()
    if (!map.hasLayer(layer)) return
    const zoom = Number(map.getZoom())
    if (!Number.isFinite(zoom) || zoom < state.settings.minZoom || zoom > state.settings.maxZoom) return

    const size = map.getSize()
    const basis = fnGetBasisVectors()
    const xLength = fnPointLength(basis.xVector)
    const yLength = fnPointLength(basis.yVector)
    if (!(xLength > 0) || !(yLength > 0)) return

    const corners = [
      L.point(0, 0),
      L.point(size.x, 0),
      L.point(size.x, size.y),
      L.point(0, size.y)
    ].map(function (containerPoint) {
      return fnProjectLatLng(map.containerPointToLatLng(containerPoint))
    }).filter(function (point) {
      return !!point
    })
    if (corners.length < 4) return
    const diagonal = Math.sqrt(
      Math.pow(corners[2].x - corners[0].x, 2) +
      Math.pow(corners[2].y - corners[0].y, 2)
    )
    const lineExtent = diagonal * 1.5
    const xDirection = fnNormalizePoint(basis.xVector)
    const yDirection = fnNormalizePoint(basis.yVector)
    const viewportCenter = fnProjectLatLng(map.getCenter())
    if (!viewportCenter) return
    const xDots = corners.map(function (corner) {
      return fnDotPoint(
        L.point(corner.x - basis.origin.x, corner.y - basis.origin.y),
        xDirection
      )
    })
    const yDots = corners.map(function (corner) {
      return fnDotPoint(
        L.point(corner.x - basis.origin.x, corner.y - basis.origin.y),
        yDirection
      )
    })
    const minVerticalIndex = Math.floor((Math.min.apply(null, xDots) - xLength) / xLength)
    const maxVerticalIndex = Math.ceil((Math.max.apply(null, xDots) + xLength) / xLength)
    const minHorizontalIndex = Math.floor((Math.min.apply(null, yDots) - yLength) / yLength)
    const maxHorizontalIndex = Math.ceil((Math.max.apply(null, yDots) + yLength) / yLength)

    for (let idx = minVerticalIndex; idx <= maxVerticalIndex; idx += 1) {
      const lineAnchor = L.point(
        basis.origin.x + (basis.xVector.x * idx),
        basis.origin.y + (basis.xVector.y * idx)
      )
      const lineCenterShift = fnDotPoint(
        L.point(viewportCenter.x - lineAnchor.x, viewportCenter.y - lineAnchor.y),
        yDirection
      )
      const anchor = L.point(
        lineAnchor.x + (yDirection.x * lineCenterShift),
        lineAnchor.y + (yDirection.y * lineCenterShift)
      )
      fnAddLinePair(
        L.point(anchor.x - (yDirection.x * lineExtent), anchor.y - (yDirection.y * lineExtent)),
        L.point(anchor.x + (yDirection.x * lineExtent), anchor.y + (yDirection.y * lineExtent))
      )
    }

    for (let idx = minHorizontalIndex; idx <= maxHorizontalIndex; idx += 1) {
      const lineAnchor = L.point(
        basis.origin.x + (basis.yVector.x * idx),
        basis.origin.y + (basis.yVector.y * idx)
      )
      const lineCenterShift = fnDotPoint(
        L.point(viewportCenter.x - lineAnchor.x, viewportCenter.y - lineAnchor.y),
        xDirection
      )
      const anchor = L.point(
        lineAnchor.x + (xDirection.x * lineCenterShift),
        lineAnchor.y + (xDirection.y * lineCenterShift)
      )
      fnAddLinePair(
        L.point(anchor.x - (xDirection.x * lineExtent), anchor.y - (xDirection.y * lineExtent)),
        L.point(anchor.x + (xDirection.x * lineExtent), anchor.y + (xDirection.y * lineExtent))
      )
    }
  }

  const fnScheduleGridRedraw = function () {
    if (state.redrawFrame) return
    const requestFrame = (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function')
      ? window.requestAnimationFrame
      : function (callback) { return window.setTimeout(callback, 0) }
    state.redrawFrame = requestFrame(fnRedrawGrid)
  }

  const fnUpdateSettings = function (partialSettings, options) {
    const opts = Object.assign({ persist: true, redraw: true }, options || {})
    state.settings = fnNormalizeGridSettings(Object.assign({}, state.settings, partialSettings || {}))
    if (!db.settings || typeof db.settings !== 'object') db.settings = {}
    db.settings.gridHelper = Object.assign({}, state.settings)
    if (opts.persist) {
      fnPersistGridSettings()
    }
    if (opts.redraw) {
      fnScheduleGridRedraw()
    }
    fnSyncRotationCenterMarker()
    fnEmitStateChange()
    return Object.assign({}, state.settings)
  }

  const fnSetOverlayEnabled = function (enabled) {
    if (enabled) {
      if (!map.hasLayer(layer)) {
        layer.addTo(map)
      } else {
        fnScheduleGridRedraw()
      }
      fnEmitStateChange()
      return true
    }
    if (map.hasLayer(layer)) {
      map.removeLayer(layer)
    } else {
      fnClearGrid()
    }
    fnEmitStateChange()
    return false
  }

  const fnSyncModalFieldValues = function ($modal) {
    if (!$modal || !$modal.length) return
    const appliedOffset = fnGetAppliedProjectedOffset()
    $modal.find('[data-grid-helper-width]').val(String(state.settings.cellWidthM))
    $modal.find('[data-grid-helper-height]').val(String(state.settings.cellHeightM))
    $modal.find('[data-grid-helper-rotation]').val(String(state.settings.rotationDeg))
    $modal.find('[data-grid-helper-min-zoom]').val(String(state.settings.minZoom))
    $modal.find('[data-grid-helper-max-zoom]').val(String(state.settings.maxZoom))
    $modal.find('[data-grid-helper-shift-step-meters]').val(String(state.shiftStepMeters.toFixed(2)))
    $modal.find('[data-grid-helper-rotate-step-deg]').val(String(state.rotateStepDeg.toFixed(2)))
    $modal.find('[data-grid-helper-shift-x]').text(fnFormatOffsetPx(appliedOffset.x))
    $modal.find('[data-grid-helper-shift-y]').text(fnFormatOffsetPx(appliedOffset.y))
  }

  const fnSetShiftButtonActiveState = function ($modal, activeDirection) {
    if (!$modal || !$modal.length) return
    $modal.find('[data-grid-helper-shift-button]').each(function () {
      const $button = $(this)
      const direction = String($button.attr('data-grid-helper-shift-button') || '').trim()
      const isActive = !!activeDirection && direction === activeDirection
      $button.toggleClass('btn-secondary', isActive)
      $button.toggleClass('btn-outline-secondary', !isActive)
      $button.attr('aria-pressed', isActive ? 'true' : 'false')
    })
  }

  const fnSetRotateButtonActiveState = function ($modal, activeDirection) {
    if (!$modal || !$modal.length) return
    $modal.find('[data-grid-helper-rotate-button]').each(function () {
      const $button = $(this)
      const direction = String($button.attr('data-grid-helper-rotate-button') || '').trim()
      const isActive = !!activeDirection && direction === activeDirection
      $button.toggleClass('btn-secondary', isActive)
      $button.toggleClass('btn-outline-secondary', !isActive)
      $button.attr('aria-pressed', isActive ? 'true' : 'false')
    })
  }

  const fnBuildRedCenterMarkerIcon = function () {
    return L.divIcon({
      className: '',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      html: '<span style="display:block;width:14px;height:14px;border-radius:50%;background:#dc3545;border:2px solid #ffffff;box-shadow:0 0 0 1px rgba(0,0,0,0.35);"></span>'
    })
  }

  const fnResolveRotationCenterTargetProjectedPoint = function () {
    if (Number.isFinite(state.settings.rotationCenterLat) && Number.isFinite(state.settings.rotationCenterLng)) {
      return fnProjectLatLng(L.latLng(state.settings.rotationCenterLat, state.settings.rotationCenterLng))
    }
    return fnProjectLatLng(map.getCenter())
  }

  const fnResolveRotationCenterGridPoint = function (basis) {
    const currentBasis = basis || fnGetBasisVectors()
    const targetPoint = fnResolveRotationCenterTargetProjectedPoint()
    return fnGetNearestGridPointToContainerPoint(currentBasis, targetPoint)
  }

  const fnCommitRotationCenterLatLng = function (latLng, options) {
    const opts = Object.assign({ persist: true }, options || {})
    if (!latLng || !Number.isFinite(Number(latLng.lat)) || !Number.isFinite(Number(latLng.lng))) return null
    const next = fnUpdateSettings({
      rotationCenterLat: Number(latLng.lat),
      rotationCenterLng: Number(latLng.lng)
    }, { persist: opts.persist, redraw: false })
    return {
      lat: Number(next.rotationCenterLat),
      lng: Number(next.rotationCenterLng)
    }
  }

  const fnEnsureRotationCenter = function () {
    if (Number.isFinite(state.settings.rotationCenterLat) && Number.isFinite(state.settings.rotationCenterLng)) return
    const basis = fnGetBasisVectors()
    const nearest = fnResolveRotationCenterGridPoint(basis)
    if (!nearest || !nearest.latLng) return
    fnCommitRotationCenterLatLng(nearest.latLng)
  }

  const fnSyncRotationCenterMarker = function () {
    if (!state.modalOpen || !state.rotationCenterMarker) return
    const nearest = fnResolveRotationCenterGridPoint()
    if (!nearest || !nearest.latLng) return
    state.rotationCenterMarker.setLatLng(nearest.latLng)
  }

  const fnResetRotationCenterToViewportCenter = function () {
    const basis = fnGetBasisVectors()
    const viewportCenter = fnProjectLatLng(map.getCenter())
    const nearest = fnGetNearestGridPointToContainerPoint(basis, viewportCenter)
    if (!nearest || !nearest.latLng) return false
    fnCommitRotationCenterLatLng(nearest.latLng)
    fnSyncRotationCenterMarker()
    return true
  }

  const fnShowRotationCenterMarker = function () {
    fnEnsureRotationCenter()
    const nearest = fnResolveRotationCenterGridPoint()
    if (!nearest || !nearest.latLng) return
    if (!state.rotationCenterMarker) {
      state.rotationCenterMarker = L.marker(nearest.latLng, {
        draggable: true,
        keyboard: false,
        autoPan: true,
        icon: fnBuildRedCenterMarkerIcon()
      })
      state.rotationCenterMarker.on('dragend', function () {
        const markerLatLng = state.rotationCenterMarker ? state.rotationCenterMarker.getLatLng() : null
        if (!markerLatLng) return
        const snapped = fnGetNearestGridPointToContainerPoint(fnGetBasisVectors(), fnProjectLatLng(markerLatLng))
        if (!snapped || !snapped.latLng) return
        fnCommitRotationCenterLatLng(snapped.latLng)
        fnSyncRotationCenterMarker()
      })
    }
    state.rotationCenterMarker.setLatLng(nearest.latLng)
    if (!map.hasLayer(state.rotationCenterMarker)) {
      state.rotationCenterMarker.addTo(map)
    }
  }

  const fnHideRotationCenterMarker = function () {
    if (!state.rotationCenterMarker) return
    if (map.hasLayer(state.rotationCenterMarker)) {
      map.removeLayer(state.rotationCenterMarker)
    }
  }

  const fnGetGeoShiftVectorByDirection = function (direction, stepMeters) {
    const centerLatLng = map.getCenter()
    const vector = {
      left: fnBuildProjectedVectorForGroundOffset(centerLatLng, 0, -stepMeters),
      right: fnBuildProjectedVectorForGroundOffset(centerLatLng, 0, stepMeters),
      up: fnBuildProjectedVectorForGroundOffset(centerLatLng, stepMeters, 0),
      down: fnBuildProjectedVectorForGroundOffset(centerLatLng, -stepMeters, 0)
    }[direction]
    if (!vector) return null
    return {
      x: Number(vector.x),
      y: Number(vector.y)
    }
  }

  const fnSetShiftStepMeters = function (rawValue) {
    state.shiftStepMeters = fnWriteShiftStepMetersToStorage(rawValue)
    return state.shiftStepMeters
  }

  const fnSetRotateStepDeg = function (rawValue) {
    state.rotateStepDeg = fnWriteRotateStepDegToStorage(rawValue)
    return state.rotateStepDeg
  }

  const fnApplyShiftStep = function (direction) {
    const appliedOffset = fnGetAppliedProjectedOffset()
    const stepMeters = fnNormalizeShiftStepMeters(state.shiftStepMeters, GRID_SHIFT_STEP_DEFAULT_M)
    const shiftVector = fnGetGeoShiftVectorByDirection(direction, stepMeters)
    if (!shiftVector) return false
    const delta = {
      left: { offsetXM: appliedOffset.x + shiftVector.x, offsetYM: appliedOffset.y + shiftVector.y },
      right: { offsetXM: appliedOffset.x + shiftVector.x, offsetYM: appliedOffset.y + shiftVector.y },
      up: { offsetXM: appliedOffset.x + shiftVector.x, offsetYM: appliedOffset.y + shiftVector.y },
      down: { offsetXM: appliedOffset.x + shiftVector.x, offsetYM: appliedOffset.y + shiftVector.y }
    }[direction]
    if (!delta) return false
    console.log('[Grid][ShiftApply] ' + JSON.stringify({
      direction: String(direction || ''),
      stepMeters: Number(stepMeters),
      shiftVector: { x: Number(shiftVector.x), y: Number(shiftVector.y) },
      appliedOffsetBefore: { x: Number(appliedOffset.x), y: Number(appliedOffset.y) },
      appliedOffsetAfter: { x: Number(delta.offsetXM), y: Number(delta.offsetYM) }
    }))
    fnUpdateSettings(delta)
    fnSyncRotationCenterMarker()
    return true
  }

  const fnApplyRotationStep = function (direction) {
    const rotateStepDeg = fnNormalizeRotateStepDeg(state.rotateStepDeg, GRID_ROTATION_STEP_DEFAULT_DEG)
    const deltaDeg = {
      left: rotateStepDeg,
      right: -rotateStepDeg
    }[direction]
    if (!Number.isFinite(deltaDeg)) return false
    const oldBasis = fnGetBasisVectorsBySettings(state.settings)
    const rotationCenter = fnResolveRotationCenterGridPoint(oldBasis)
    if (!rotationCenter) return false
    const oldCenterPoint = rotationCenter.containerPoint
    const nextRotationDeg = Number(state.settings.rotationDeg) + deltaDeg
    const nextSettingsSeed = Object.assign({}, state.settings, { rotationDeg: nextRotationDeg })
    const nextBasis = fnGetBasisVectorsBySettings(nextSettingsSeed)
    const nextCenterPoint = fnBuildGridPointFromIndices(nextBasis, rotationCenter.idxX, rotationCenter.idxY)
    if (!nextCenterPoint) return false
    const appliedOffset = fnGetAppliedProjectedOffset()
    const offsetDeltaX = oldCenterPoint.x - nextCenterPoint.containerPoint.x
    const offsetDeltaY = oldCenterPoint.y - nextCenterPoint.containerPoint.y
    fnUpdateSettings({
      rotationDeg: nextRotationDeg,
      offsetXM: appliedOffset.x + offsetDeltaX,
      offsetYM: appliedOffset.y + offsetDeltaY
    })
    fnSyncRotationCenterMarker()
    return true
  }

  const fnCommitZoomField = function ($modal, zoomKey, rawValue) {
    const key = String(zoomKey || '').trim()
    if (key !== 'minZoom' && key !== 'maxZoom') return null
    const nextSettings = fnUpdateSettings({ [key]: rawValue })
    fnSyncModalFieldValues($modal)
    return nextSettings
  }

  const fnSetWaypointFitStatus = function ($modal, text, status) {
    if (!$modal || !$modal.length) return
    const $status = $modal.find('[data-grid-helper-fit-status]').first()
    if (!$status.length) return
    $status.text(String(text || ''))
    $status.toggleClass('text-danger', status === 'error')
    $status.toggleClass('text-success', status === 'success')
    $status.toggleClass('text-muted', status !== 'error' && status !== 'success')
  }

  const fnGetWaypointSpacingToleranceMeters = function (spacingMeters) {
    return Math.max(
      GRID_WAYPOINT_SPACING_TOLERANCE_MIN_M,
      Math.abs(Number(spacingMeters) || 0) * GRID_WAYPOINT_SPACING_TOLERANCE_RATIO
    )
  }

  const fnCollectWaypointFitPoints = function (waypoints) {
    const rows = []
    const list = Array.isArray(waypoints) ? waypoints : []
    list.forEach(function (waypoint, index) {
      const latLng = fnGetWaypointLatLng(waypoint)
      if (!latLng) return
      const projectedPoint = fnProjectLatLng(latLng)
      if (!projectedPoint) return
      rows.push({
        index: index,
        waypoint: waypoint,
        latLng: latLng,
        projectedPoint: projectedPoint
      })
    })
    return rows
  }

  const fnGetWaypointPointDistanceMeters = function (leftRow, rightRow) {
    if (!leftRow || !rightRow || !leftRow.latLng || !rightRow.latLng) return NaN
    return Number(map.distance(leftRow.latLng, rightRow.latLng))
  }

  const fnGetWaypointBearingBetweenRows = function (fromRow, toRow) {
    if (!fromRow || !toRow || !fromRow.latLng || !toRow.latLng) return NaN
    return fnNormalizeBearing(bearingBetweenLatLng(
      fromRow.latLng.lat,
      fromRow.latLng.lng,
      toRow.latLng.lat,
      toRow.latLng.lng
    ))
  }

  const fnEstimateWaypointSpacingMeters = function (pointRows) {
    const rows = Array.isArray(pointRows) ? pointRows : []
    const nearestDistances = []
    rows.forEach(function (row, rowIndex) {
      let nearestDistanceM = Infinity
      rows.forEach(function (otherRow, otherIndex) {
        if (rowIndex === otherIndex) return
        const distanceM = fnGetWaypointPointDistanceMeters(row, otherRow)
        if (!(distanceM >= GRID_WAYPOINT_MIN_DISTANCE_M)) return
        if (distanceM < nearestDistanceM) nearestDistanceM = distanceM
      })
      if (Number.isFinite(nearestDistanceM)) {
        nearestDistances.push(nearestDistanceM)
      }
    })
    return fnMedian(nearestDistances)
  }

  const fnGetWaypointFitCentroidProjected = function (pointRows) {
    const rows = Array.isArray(pointRows) ? pointRows : []
    if (!rows.length) return null
    let sumX = 0
    let sumY = 0
    rows.forEach(function (row) {
      sumX += Number(row.projectedPoint.x)
      sumY += Number(row.projectedPoint.y)
    })
    return L.point(sumX / rows.length, sumY / rows.length)
  }

  const fnGetNearestWaypointFitPointToProjected = function (targetProjectedPoint, pointRows, excludedIndexes) {
    const rows = Array.isArray(pointRows) ? pointRows : []
    if (!targetProjectedPoint || !rows.length) return null
    const predictedLatLng = fnUnprojectPoint(targetProjectedPoint)
    if (!predictedLatLng) return null
    const excluded = excludedIndexes instanceof Set ? excludedIndexes : new Set()
    let bestMatch = null
    rows.forEach(function (row) {
      if (!row || excluded.has(row.index)) return
      const distanceM = Number(map.distance(predictedLatLng, row.latLng))
      if (!Number.isFinite(distanceM)) return
      if (!bestMatch || distanceM < bestMatch.distanceM) {
        bestMatch = {
          row: row,
          distanceM: distanceM
        }
      }
    })
    return bestMatch
  }

  const fnFindBestWaypointFitCell = function (pointRows, spacingMeters) {
    const rows = Array.isArray(pointRows) ? pointRows : []
    if (rows.length < GRID_WAYPOINT_MIN_POINTS) return null
    const spacingToleranceM = Math.max(
      fnGetWaypointSpacingToleranceMeters(spacingMeters),
      GRID_WAYPOINT_CELL_PREDICTION_TOLERANCE_M
    )
    const missionCentroidProjected = fnGetWaypointFitCentroidProjected(rows)
    const bestByKey = new Map()
    rows.forEach(function (originRow) {
      const neighborRows = rows.filter(function (candidateRow) {
        if (!candidateRow || candidateRow.index === originRow.index) return false
        const distanceM = fnGetWaypointPointDistanceMeters(originRow, candidateRow)
        return distanceM >= GRID_WAYPOINT_MIN_DISTANCE_M &&
          Math.abs(distanceM - spacingMeters) <= (spacingToleranceM * 2)
      })
      for (let i = 0; i < neighborRows.length; i += 1) {
        const firstNeighborRow = neighborRows[i]
        const firstDistanceM = fnGetWaypointPointDistanceMeters(originRow, firstNeighborRow)
        const firstBearingDeg = fnGetWaypointBearingBetweenRows(originRow, firstNeighborRow)
        for (let j = i + 1; j < neighborRows.length; j += 1) {
          const secondNeighborRow = neighborRows[j]
          const secondDistanceM = fnGetWaypointPointDistanceMeters(originRow, secondNeighborRow)
          const secondBearingDeg = fnGetWaypointBearingBetweenRows(originRow, secondNeighborRow)
          const angleBetweenDeg = Math.abs(fnSignedBearingDiff(firstBearingDeg, secondBearingDeg))
          const rightAngleDeltaDeg = Math.abs(90 - angleBetweenDeg)
          if (rightAngleDeltaDeg > GRID_WAYPOINT_RIGHT_ANGLE_TOLERANCE_DEG) continue
          const predictedProjectedPoint = L.point(
            Number(firstNeighborRow.projectedPoint.x) + Number(secondNeighborRow.projectedPoint.x) - Number(originRow.projectedPoint.x),
            Number(firstNeighborRow.projectedPoint.y) + Number(secondNeighborRow.projectedPoint.y) - Number(originRow.projectedPoint.y)
          )
          const oppositeMatch = fnGetNearestWaypointFitPointToProjected(
            predictedProjectedPoint,
            rows,
            new Set([originRow.index, firstNeighborRow.index, secondNeighborRow.index])
          )
          if (!oppositeMatch || oppositeMatch.distanceM > (spacingToleranceM * 2)) continue
          const oppositeRow = oppositeMatch.row
          const firstOppositeDistanceM = fnGetWaypointPointDistanceMeters(firstNeighborRow, oppositeRow)
          const secondOppositeDistanceM = fnGetWaypointPointDistanceMeters(secondNeighborRow, oppositeRow)
          if (
            Math.abs(firstOppositeDistanceM - secondDistanceM) > (spacingToleranceM * 2) ||
            Math.abs(secondOppositeDistanceM - firstDistanceM) > (spacingToleranceM * 2)
          ) {
            continue
          }
          const cellRows = [originRow, firstNeighborRow, oppositeRow, secondNeighborRow]
          const sortedIndexes = cellRows.map(function (row) { return row.index }).sort(function (left, right) { return left - right })
          const cellKey = sortedIndexes.join(':')
          const cellCentroidProjected = L.point(
            cellRows.reduce(function (sum, row) { return sum + Number(row.projectedPoint.x) }, 0) / cellRows.length,
            cellRows.reduce(function (sum, row) { return sum + Number(row.projectedPoint.y) }, 0) / cellRows.length
          )
          const centerDistanceProjectedM = missionCentroidProjected
            ? fnPointLength(L.point(
                Number(cellCentroidProjected.x) - Number(missionCentroidProjected.x),
                Number(cellCentroidProjected.y) - Number(missionCentroidProjected.y)
              ))
            : 0
          const score = Number(oppositeMatch.distanceM) +
            Math.abs(firstOppositeDistanceM - secondDistanceM) +
            Math.abs(secondOppositeDistanceM - firstDistanceM) +
            (rightAngleDeltaDeg * 0.01) +
            (centerDistanceProjectedM * 0.01)
          const candidate = {
            originRow: originRow,
            firstNeighborRow: firstNeighborRow,
            secondNeighborRow: secondNeighborRow,
            oppositeRow: oppositeRow,
            cellRows: cellRows,
            sideWidthM: firstDistanceM,
            sideHeightM: secondDistanceM,
            firstBearingDeg: firstBearingDeg,
            secondBearingDeg: secondBearingDeg,
            rightAngleDeltaDeg: rightAngleDeltaDeg,
            predictionResidualM: Number(oppositeMatch.distanceM),
            centerDistanceProjectedM: centerDistanceProjectedM,
            score: score
          }
          const currentBest = bestByKey.get(cellKey)
          if (!currentBest || score < currentBest.score) {
            bestByKey.set(cellKey, candidate)
          }
        }
      }
    })
    const candidates = Array.from(bestByKey.values()).sort(function (left, right) {
      if (left.centerDistanceProjectedM !== right.centerDistanceProjectedM) {
        return left.centerDistanceProjectedM - right.centerDistanceProjectedM
      }
      return left.score - right.score
    })
    return candidates.length ? candidates[0] : null
  }

  const fnEvaluateWaypointFitCandidate = function (settings, pointRows, spacingMeters) {
    const basis = fnGetBasisVectorsBySettings(settings)
    const rows = Array.isArray(pointRows) ? pointRows : []
    if (!rows.length) return null
    const solvedRows = []
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i]
      const solvedIndices = fnSolveGridIndices(basis, row.projectedPoint)
      if (!solvedIndices) return null
      const snappedGridPoint = fnBuildGridPointFromIndices(
        basis,
        Math.round(solvedIndices.idxX),
        Math.round(solvedIndices.idxY)
      )
      if (!snappedGridPoint || !snappedGridPoint.latLng) return null
      const residualM = Number(map.distance(row.latLng, snappedGridPoint.latLng))
      if (!Number.isFinite(residualM)) return null
      solvedRows.push({
        pointRow: row,
        residualM: residualM,
        idxX: Math.round(solvedIndices.idxX),
        idxY: Math.round(solvedIndices.idxY)
      })
    }
    solvedRows.sort(function (left, right) { return left.residualM - right.residualM })
    const corePointCount = Math.max(
      GRID_WAYPOINT_MIN_POINTS,
      Math.min(solvedRows.length, Math.ceil(solvedRows.length * GRID_WAYPOINT_CORE_SHARE))
    )
    const coreRows = solvedRows.slice(0, corePointCount)
    const matchToleranceM = Math.max(
      GRID_WAYPOINT_CELL_PREDICTION_TOLERANCE_M,
      fnGetWaypointSpacingToleranceMeters(spacingMeters) * 2.5
    )
    const matchedRows = solvedRows.filter(function (row) {
      return row.residualM <= matchToleranceM
    })
    const uniqueCoreX = new Set(coreRows.map(function (row) { return row.idxX }))
    const uniqueCoreY = new Set(coreRows.map(function (row) { return row.idxY }))
    const coreResidualSumM = coreRows.reduce(function (sum, row) {
      return sum + Number(row.residualM)
    }, 0)
    return {
      totalPointCount: solvedRows.length,
      corePointCount: coreRows.length,
      matchedPointCount: matchedRows.length,
      matchedShare: matchedRows.length / Math.max(1, solvedRows.length),
      coreMeanResidualM: coreResidualSumM / Math.max(1, coreRows.length),
      coreMaxResidualM: coreRows.length ? coreRows[coreRows.length - 1].residualM : 0,
      overallMaxResidualM: solvedRows.length ? solvedRows[solvedRows.length - 1].residualM : 0,
      uniqueCoreXCount: uniqueCoreX.size,
      uniqueCoreYCount: uniqueCoreY.size
    }
  }

  const fnBuildWaypointFitSettingsCandidates = function (cellCandidate) {
    const candidate = cellCandidate && typeof cellCandidate === 'object' ? cellCandidate : null
    if (!candidate) return []
    const anchorRows = Array.isArray(candidate.cellRows) ? candidate.cellRows : []
    const rotationCandidates = []
    const rotationSeedList = [
      candidate.firstBearingDeg,
      candidate.secondBearingDeg
    ]
    rotationSeedList.forEach(function (seedBearingDeg) {
      const baseRotationDeg = fnBearingToRotationDeg(seedBearingDeg)
      if (!Number.isFinite(baseRotationDeg)) return
      ;[
        fnNormalizeBearing(baseRotationDeg),
        fnNormalizeBearing(baseRotationDeg - 90),
        fnNormalizeBearing(baseRotationDeg + 90),
        fnNormalizeBearing(baseRotationDeg + 180)
      ].forEach(function (rotationDeg) {
        if (!Number.isFinite(rotationDeg)) return
        rotationCandidates.push(rotationDeg)
      })
    })
    const sizeVariants = [
      {
        cellWidthM: candidate.sideWidthM,
        cellHeightM: candidate.sideHeightM
      },
      {
        cellWidthM: candidate.sideHeightM,
        cellHeightM: candidate.sideWidthM
      }
    ]
    const seenKeys = new Set()
    const settingsCandidates = []
    anchorRows.forEach(function (anchorRow) {
      if (!anchorRow || !anchorRow.latLng) return
      rotationCandidates.forEach(function (rotationDeg) {
        sizeVariants.forEach(function (sizeVariant) {
          const key = [
            anchorRow.index,
            Math.round(Number(rotationDeg) * 1000) / 1000,
            Math.round(Number(sizeVariant.cellWidthM) * 1000) / 1000,
            Math.round(Number(sizeVariant.cellHeightM) * 1000) / 1000
          ].join(':')
          if (seenKeys.has(key)) return
          seenKeys.add(key)
          settingsCandidates.push({
            anchorIndex: anchorRow.index,
            settings: Object.assign({}, state.settings, {
              anchorLat: anchorRow.latLng.lat,
              anchorLng: anchorRow.latLng.lng,
              offsetXM: 0,
              offsetYM: 0,
              offsetXPx: 0,
              offsetYPx: 0,
              offsetZoom: 0,
              rotationCenterLat: anchorRow.latLng.lat,
              rotationCenterLng: anchorRow.latLng.lng,
              rotationDeg: rotationDeg,
              cellWidthM: sizeVariant.cellWidthM,
              cellHeightM: sizeVariant.cellHeightM
            })
          })
        })
      })
    })
    return settingsCandidates
  }

  const fnFitGridToWaypoints = function ($modal) {
    const missionState = (typeof fnGetImportMissionState === 'function') ? fnGetImportMissionState() : null
    const allWaypoints = Array.isArray(missionState && missionState.waypoints)
      ? missionState.waypoints.filter(function (point) { return !!point })
      : []
    if (!allWaypoints.length) {
      fnSetWaypointFitStatus($modal, fnT('grid.fit_error_no_waypoints'), 'error')
      return null
    }
    const pointRows = fnCollectWaypointFitPoints(allWaypoints)
    if (pointRows.length < GRID_WAYPOINT_MIN_POINTS) {
      fnSetWaypointFitStatus($modal, fnT('grid.fit_error_need_four_points'), 'error')
      return null
    }
    const spacingMeters = fnEstimateWaypointSpacingMeters(pointRows)
    if (!Number.isFinite(spacingMeters)) {
      fnSetWaypointFitStatus($modal, fnT('grid.fit_error_no_regular_distribution'), 'error')
      return null
    }
    if (!(spacingMeters >= GRID_WAYPOINT_MIN_DISTANCE_M)) {
      fnSetWaypointFitStatus($modal, fnT('grid.fit_error_spacing_too_small'), 'error')
      return null
    }
    const cellCandidate = fnFindBestWaypointFitCell(pointRows, spacingMeters)
    if (!cellCandidate) {
      fnSetWaypointFitStatus($modal, fnT('grid.fit_error_no_regular_distribution'), 'error')
      return null
    }
    const settingsCandidates = fnBuildWaypointFitSettingsCandidates(cellCandidate)
    if (!settingsCandidates.length) {
      fnSetWaypointFitStatus($modal, fnT('grid.fit_error_residual'), 'error')
      return null
    }
    const evaluatedCandidates = settingsCandidates.map(function (candidate) {
      const evaluation = fnEvaluateWaypointFitCandidate(candidate.settings, pointRows, spacingMeters)
      if (!evaluation) return null
      return {
        anchorIndex: candidate.anchorIndex,
        settings: candidate.settings,
        evaluation: evaluation
      }
    }).filter(Boolean)
    if (!evaluatedCandidates.length) {
      fnSetWaypointFitStatus($modal, fnT('grid.fit_error_residual'), 'error')
      return null
    }
    evaluatedCandidates.sort(function (left, right) {
      if (left.evaluation.matchedPointCount !== right.evaluation.matchedPointCount) {
        return right.evaluation.matchedPointCount - left.evaluation.matchedPointCount
      }
      const leftCoverage = left.evaluation.uniqueCoreXCount * left.evaluation.uniqueCoreYCount
      const rightCoverage = right.evaluation.uniqueCoreXCount * right.evaluation.uniqueCoreYCount
      if (leftCoverage !== rightCoverage) {
        return rightCoverage - leftCoverage
      }
      if (left.evaluation.coreMeanResidualM !== right.evaluation.coreMeanResidualM) {
        return left.evaluation.coreMeanResidualM - right.evaluation.coreMeanResidualM
      }
      if (left.evaluation.coreMaxResidualM !== right.evaluation.coreMaxResidualM) {
        return left.evaluation.coreMaxResidualM - right.evaluation.coreMaxResidualM
      }
      return left.anchorIndex - right.anchorIndex
    })
    const bestCandidate = evaluatedCandidates[0]
    const residualToleranceM = Math.max(
      GRID_WAYPOINT_CELL_PREDICTION_TOLERANCE_M,
      fnGetWaypointSpacingToleranceMeters(Math.max(cellCandidate.sideWidthM, cellCandidate.sideHeightM)) * 2.5
    )
    const rejectReasons = []
    if (bestCandidate.evaluation.matchedShare < GRID_WAYPOINT_CLUSTER_MIN_SHARE) rejectReasons.push('matched-share')
    if (bestCandidate.evaluation.uniqueCoreXCount < 2) rejectReasons.push('core-x-span')
    if (bestCandidate.evaluation.uniqueCoreYCount < 2) rejectReasons.push('core-y-span')
    if (bestCandidate.evaluation.coreMeanResidualM > residualToleranceM) rejectReasons.push('core-mean-residual')
    if (bestCandidate.evaluation.coreMaxResidualM > (residualToleranceM * 1.5)) rejectReasons.push('core-max-residual')
    if (rejectReasons.length) {
      fnSetWaypointFitStatus($modal, fnT('grid.fit_error_residual'), 'error')
      return null
    }
    const spacingText = (
      (Math.round(Number(bestCandidate.settings.cellWidthM) * 1000) / 1000).toFixed(3).replace(/\.?0+$/, '') +
      ' x ' +
      (Math.round(Number(bestCandidate.settings.cellHeightM) * 1000) / 1000).toFixed(3).replace(/\.?0+$/, '')
    )
    fnUpdateSettings(bestCandidate.settings)
    fnSetOverlayEnabled(true)
    fnSyncRotationCenterMarker()
    fnSyncModalFieldValues($modal)
    fnSetWaypointFitStatus($modal, fnTF('grid.fit_success', {
      pointCount: bestCandidate.evaluation.matchedPointCount,
      segmentCount: Math.max(0, bestCandidate.evaluation.matchedPointCount - 1),
      spacingMeters: spacingText,
      residualMeters: (Math.round(bestCandidate.evaluation.coreMeanResidualM * 1000) / 1000).toFixed(3).replace(/\.?0+$/, '')
    }), 'success')
    return {
      cellCandidate: cellCandidate,
      candidate: bestCandidate
    }
  }

  const fnOpenSettings = function () {
    const areaSwal = fnGetSwal()
    const tpl = $body.find('[data-tpl-grid-helper-popup]').html() || ''
    if (!areaSwal || !tpl) return null
    return areaSwal.fire({
      title: fnT('grid.modal_title'),
      width: 430,
      position: 'bottom-right',
      backdrop: false,
      showConfirmButton: false,
      showCancelButton: false,
      html: tpl,
      didOpen: function (modal) {
        const $modal = $(modal)
        const $window = $(window)
        const keyToDirection = {
          ArrowLeft: 'left',
          ArrowRight: 'right',
          ArrowUp: 'up',
          ArrowDown: 'down',
          a: 'left',
          A: 'left',
          d: 'right',
          D: 'right',
          w: 'up',
          W: 'up',
          s: 'down',
          S: 'down'
        }
        const keyToRotation = {
          q: 'left',
          Q: 'left',
          e: 'right',
          E: 'right'
        }

        state.modalOpen = true
        fnSetOverlayEnabled(true)
        fnSyncModalFieldValues($modal)
        fnSetWaypointFitStatus($modal, '', 'idle')
        fnSetShiftButtonActiveState($modal, '')
        fnSetRotateButtonActiveState($modal, '')
        fnResetRotationCenterToViewportCenter()
        fnShowRotationCenterMarker()

        $modal.on('input change', '[data-grid-helper-width]', function () {
          fnUpdateSettings({ cellWidthM: $(this).val() })
        })
        $modal.on('input change', '[data-grid-helper-height]', function () {
          fnUpdateSettings({ cellHeightM: $(this).val() })
        })
        $modal.on('input change', '[data-grid-helper-rotation]', function () {
          fnUpdateSettings({ rotationDeg: $(this).val() })
        })
        $modal.on('input change', '[data-grid-helper-shift-step-meters]', function () {
          fnSetShiftStepMeters($(this).val())
        })
        $modal.on('focusout', '[data-grid-helper-shift-step-meters]', function () {
          fnSyncModalFieldValues($modal)
        })
        $modal.on('input change', '[data-grid-helper-rotate-step-deg]', function () {
          fnSetRotateStepDeg($(this).val())
        })
        $modal.on('focusout', '[data-grid-helper-rotate-step-deg]', function () {
          fnSyncModalFieldValues($modal)
        })
        $modal.on('keydown', '[data-grid-helper-min-zoom], [data-grid-helper-max-zoom]', function (event) {
          if (String(event.key || '') === 'Enter') {
            event.preventDefault()
            this.blur()
          }
        })
        $modal.on('focusout', '[data-grid-helper-min-zoom]', function () {
          fnCommitZoomField($modal, 'minZoom', $(this).val())
        })
        $modal.on('focusout', '[data-grid-helper-max-zoom]', function () {
          fnCommitZoomField($modal, 'maxZoom', $(this).val())
        })
        $modal.on('click', '[data-grid-helper-shift-button]', function () {
          const direction = String($(this).attr('data-grid-helper-shift-button') || '').trim()
          if (!fnApplyShiftStep(direction)) return
          fnSyncModalFieldValues($modal)
        })
        $modal.on('click', '[data-grid-helper-rotate-button]', function () {
          const direction = String($(this).attr('data-grid-helper-rotate-button') || '').trim()
          if (!fnApplyRotationStep(direction)) return
          fnSyncModalFieldValues($modal)
        })
        $modal.on('click', '[data-grid-helper-fit-waypoints]', function () {
          fnFitGridToWaypoints($modal)
        })
        $modal.on('click', '[data-grid-helper-close]', function () {
          areaSwal.close()
        })

        state.modalKeydownCapture = function (event) {
          if (!state.modalOpen) return
          if (event.altKey || event.ctrlKey || event.metaKey) return
          if (fnIsEditableElement(document.activeElement) || fnIsEditableElement(event.target)) return
          const key = String(event.key || '')
          const direction = keyToDirection[key]
          const rotationDirection = keyToRotation[key]
          event.preventDefault()
          event.stopPropagation()
          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation()
          }
          if (!direction && !rotationDirection) return
          fnSetShiftButtonActiveState($modal, direction || '')
          fnSetRotateButtonActiveState($modal, rotationDirection || '')
          const actionOk = direction ? fnApplyShiftStep(direction) : fnApplyRotationStep(rotationDirection)
          if (actionOk) {
            fnSyncModalFieldValues($modal)
          }
        }

        state.modalKeyupCapture = function (event) {
          if (!state.modalOpen) return
          if (event.altKey || event.ctrlKey || event.metaKey) return
          if (fnIsEditableElement(document.activeElement) || fnIsEditableElement(event.target)) return
          const key = String(event.key || '')
          const direction = keyToDirection[key]
          const rotationDirection = keyToRotation[key]
          event.preventDefault()
          event.stopPropagation()
          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation()
          }
          if (!direction && !rotationDirection) return
          fnSetShiftButtonActiveState($modal, '')
          fnSetRotateButtonActiveState($modal, '')
        }

        if (document && typeof document.addEventListener === 'function') {
          document.addEventListener('keydown', state.modalKeydownCapture, true)
          document.addEventListener('keyup', state.modalKeyupCapture, true)
        }

        $window
          .off('blur.portalGridHelperModal')
          .on('blur.portalGridHelperModal', function () {
            fnSetShiftButtonActiveState($modal, '')
            fnSetRotateButtonActiveState($modal, '')
          })
      },
      didClose: function () {
        state.modalOpen = false
        fnHideRotationCenterMarker()
        if (document && typeof document.removeEventListener === 'function') {
          if (state.modalKeydownCapture) {
            document.removeEventListener('keydown', state.modalKeydownCapture, true)
          }
          if (state.modalKeyupCapture) {
            document.removeEventListener('keyup', state.modalKeyupCapture, true)
          }
        }
        state.modalKeydownCapture = null
        state.modalKeyupCapture = null
        $(window).off('blur.portalGridHelperModal')
      }
    })
  }

  const fnGetNearestAxisBearing = function (axisBearing) {
    const currentBearing = fnNormalizeBearing(axisBearing)
    if (!Number.isFinite(currentBearing)) return NaN
    const baseBearing = fnNormalizeBearing(state.settings.rotationDeg)
    const candidates = [
      baseBearing,
      fnNormalizeBearing(baseBearing + 90),
      fnNormalizeBearing(baseBearing + 180),
      fnNormalizeBearing(baseBearing + 270)
    ]
    let bestBearing = NaN
    let bestDiff = Infinity
    candidates.forEach(function (candidate) {
      const diff = Math.abs(fnSignedBearingDiff(currentBearing, candidate))
      if (diff < bestDiff) {
        bestDiff = diff
        bestBearing = candidate
      }
    })
    return bestBearing
  }

  layer.on('add', function () {
    fnScheduleGridRedraw()
    fnEmitStateChange()
  })
  layer.on('remove', function () {
    fnClearGrid()
    fnEmitStateChange()
  })

  map.on('moveend zoomend resize', function () {
    fnScheduleGridRedraw()
    if (state.modalOpen) {
      fnResetRotationCenterToViewportCenter()
    } else {
      fnSyncRotationCenterMarker()
    }
  })
  map.on('zoomend', function () {
    fnEmitStateChange()
  })

  $body
    .off('click.portalGridHelper', '[data-leaf-grid-helper]')
    .on('click.portalGridHelper', '[data-leaf-grid-helper]', function () {
      fnOpenSettings()
    })

  if (typeof window !== 'undefined') {
    window.PORTAL_GRID_HELPER = {
      openSettings: fnOpenSettings,
      activate: function () { return fnSetOverlayEnabled(true) },
      fitToWaypoints: function () { return fnFitGridToWaypoints(null) },
      getSettings: function () { return Object.assign({}, state.settings) },
      getNearestGridPoint: fnGetNearestGridPoint,
      getNearestAxisBearing: fnGetNearestAxisBearing,
      getVisibilityState: fnGetVisibilityState,
      requestRedraw: fnScheduleGridRedraw
    }
  }

  return {
    layer: layer,
    openSettings: fnOpenSettings,
    activate: function () { return fnSetOverlayEnabled(true) },
    fitToWaypoints: function () { return fnFitGridToWaypoints(null) },
    getSettings: function () { return Object.assign({}, state.settings) },
    getNearestGridPoint: fnGetNearestGridPoint,
    getNearestAxisBearing: fnGetNearestAxisBearing,
    getVisibilityState: fnGetVisibilityState,
    requestRedraw: fnScheduleGridRedraw
  }
}

export {
  createMapGridHelper
}
